/* import __COLOCATED_TEMPLATE__ from './cx-distribution.hbs'; */
/* RESPONSIBLE TEAM: team-ai-insights */
import Component from '@glimmer/component';
import type {
  Options,
  SeriesOptionsType,
  AxisLabelsFormatterContextObject,
  Point,
} from 'highcharts';
import { interpolatePercentileRank } from 'embercom/lib/reporting/cx-benchmarks/cx-benchmark-service';
import {
  buildDensityCurve,
  getValueAtPercentile,
} from 'embercom/lib/reporting/cx-benchmarks/density-curve';
import type { CxBenchmarkGroup } from './types';

function ordinalSuffix(n: number): string {
  let lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return 'th';
  }
  let lastDigit = n % 10;
  if (lastDigit === 1) {
    return 'st';
  }
  if (lastDigit === 2) {
    return 'nd';
  }
  if (lastDigit === 3) {
    return 'rd';
  }
  return 'th';
}

interface Signature {
  Args: {
    benchmarkGroup: CxBenchmarkGroup | undefined;
    industryLabel: string;
    appCxScore: number | undefined;
  };
}

interface TickEntry {
  pos: number;
  label: string;
}

export default class CxDistribution extends Component<Signature> {
  get appCxScore(): number {
    return this.args.appCxScore ?? 0;
  }

  get hasAppCxScore(): boolean {
    return this.args.appCxScore !== undefined;
  }

  get appCxScorePercent(): string {
    return `${(this.appCxScore * 100).toFixed(1)}%`;
  }

  get percentileRank(): number {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return 0;
    }
    return Math.round(interpolatePercentileRank(group, this.appCxScore));
  }

  get hasBenchmarkData(): boolean {
    return this.args.benchmarkGroup !== undefined && this.hasAppCxScore;
  }

  get curveData(): [number, number][] {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return [];
    }
    return buildDensityCurve(group.percentiles);
  }

  private get tickMap(): TickEntry[] {
    let group = this.args.benchmarkGroup;
    if (!group) {
      return [];
    }
    let percentiles = group.percentiles;
    let curve = this.curveData;
    if (curve.length === 0) {
      return [];
    }

    let minX = curve[0][0];
    let maxX = curve[curve.length - 1][0];

    return [
      { pos: minX, label: '0th' },
      { pos: getValueAtPercentile(percentiles, 0.25), label: '25th' },
      { pos: getValueAtPercentile(percentiles, 0.5), label: '50th' },
      { pos: getValueAtPercentile(percentiles, 0.75), label: '75th' },
      { pos: maxX, label: '100th' },
    ];
  }

  get highchartsSeries(): SeriesOptionsType[] {
    return [
      {
        type: 'areaspline' as const,
        data: this.curveData,
        fillColor: 'var(--neutral-container-emphasis)',
        lineWidth: 0,
        lineColor: 'transparent',
        marker: { enabled: false, states: { hover: { enabled: false } } },
        enableMouseTracking: true,
        animation: false,
        threshold: 0,
      },
    ];
  }

  get highchartsOptions(): Options {
    let ticks = this.tickMap;
    let middleTicks = ticks.slice(1, -1);
    let tickPositions = middleTicks.map((t) => t.pos);
    let percentileRank = this.percentileRank;
    let appCxScore = this.appCxScore;
    let benchmarkGroup = this.args.benchmarkGroup;
    let curveMinX = this.curveData[0]?.[0] ?? 0;

    return {
      chart: {
        type: 'areaspline',
        height: 300,
        spacing: [40, 0, 25, 0],
        animation: false,
        backgroundColor: 'transparent',
        style: {
          fontFamily: 'inherit',
        },
      },

      title: { text: undefined },
      legend: { enabled: false },
      credits: { enabled: false },
      tooltip: {
        useHTML: true,
        outside: true,
        backgroundColor: 'var(--base-module)',
        borderColor: 'var(--neutral-border)',
        borderRadius: 12,
        shadow: {
          color: 'rgba(0,0,0,0.08)',
          offsetX: 0,
          offsetY: 2,
          width: 8,
        },
        padding: 12,
        style: {
          color: 'var(--text-default)',
          fontSize: '14px',
        },
        formatter(this: Point): string {
          if (!benchmarkGroup) {
            return '';
          }
          let cxScore = this.x;
          let hoveredPercentile: number;
          let p5Value = benchmarkGroup.percentiles.p5;
          if (cxScore < p5Value) {
            let range = p5Value - curveMinX;
            hoveredPercentile =
              range > 0 ? Math.max(0, Math.round(((cxScore - curveMinX) / range) * 5)) : 0;
          } else if (cxScore > benchmarkGroup.percentiles.p100) {
            hoveredPercentile = 100;
          } else {
            hoveredPercentile = Math.round(interpolatePercentileRank(benchmarkGroup, cxScore));
          }
          let scorePercent = (Math.max(0, Math.min(1, cxScore)) * 100).toFixed(1);
          let suffix = ordinalSuffix(hoveredPercentile);
          return `<div style="text-align:center;line-height:1.6;">
            <span style="font-weight:600;font-size:15px;">${hoveredPercentile}${suffix} percentile</span><br>
            <span>CX score: ${scorePercent}%</span>
          </div>`;
        },
      },

      yAxis: {
        visible: false,
        gridLineWidth: 0,
      },

      xAxis: {
        crosshair: {
          width: 1,
          color: 'var(--text-muted)',
          zIndex: 3,
        },
        tickPositions,
        labels: {
          useHTML: true,
          formatter(this: AxisLabelsFormatterContextObject) {
            let closest: TickEntry | null = null;
            let minDist = Infinity;
            for (let entry of middleTicks) {
              let dist = Math.abs(Number(this.value) - entry.pos);
              if (dist < minDist) {
                minDist = dist;
                closest = entry;
              }
            }
            if (!closest) {
              return '';
            }
            return `<span style="color:var(--text-disabled);font-size:13px;">${closest.label}</span>`;
          },
        },
        lineColor: 'transparent',
        tickLength: 0,
        gridLineWidth: 0,
        plotLines: [
          // Percentile divider lines — background-colored to "cut" through the fill
          ...ticks.slice(1).map((t) => ({
            value: t.pos,
            color: 'var(--base-module)',
            width: 3,
            zIndex: 4,
          })),
          // Edge labels — rendered as plotLine labels so they support native left/right alignment
          ...(ticks.length > 0
            ? [
                {
                  value: ticks[0].pos,
                  width: 0,
                  zIndex: 4,
                  label: {
                    useHTML: true,
                    text: '<span style="color:var(--text-disabled);font-size:13px;">0th</span><br><span style="color:var(--text-disabled);font-size:12px;">Percentile</span>',
                    rotation: 0,
                    align: 'left' as const,
                    verticalAlign: 'bottom' as const,
                    y: 15,
                    x: 0,
                  },
                },
                {
                  value: ticks[ticks.length - 1].pos,
                  width: 0,
                  zIndex: 4,
                  label: {
                    useHTML: true,
                    text: '<span style="color:var(--text-disabled);font-size:13px;">100th</span>',
                    rotation: 0,
                    align: 'right' as const,
                    verticalAlign: 'bottom' as const,
                    y: 15,
                    x: 0,
                  },
                },
              ]
            : []),
          // "You are here" marker
          {
            value: appCxScore,
            color: 'var(--text-default)',
            width: 1.5,
            zIndex: 5,
            label: {
              useHTML: true,
              text: `<span style="display:inline-block;background:var(--neutral-fill);color:var(--text-on-neutral);border-radius:12px;padding:2px 8px;font-size:13px;font-weight:600;line-height:1.3;">${percentileRank}${ordinalSuffix(percentileRank)}</span>`,
              rotation: 0,
              verticalAlign: 'top',
              textAlign: 'center',
              y: -14,
              x: 0,
            },
          },
        ],
      },
    };
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Reporting::Bespoke::FinPerformance::CxBenchmarkPanel::CxDistribution': typeof CxDistribution;
    'reporting/bespoke/fin-performance/cx-benchmark-panel/cx-distribution': typeof CxDistribution;
  }
}
